/**
 * Shared submit-handler factory for the five non-intake typed encounter
 * forms (general, case-management, discharge, clinical-follow-up,
 * cocm-follow-up).
 *
 * Each non-intake hook previously implemented the same control flow:
 *   1. Validate `profile` + `patient`.
 *   2. `flat = toBundleInput(type, formData)`.
 *   3. `originalFlat = toBundleInput(type, originalFormData)` (when set).
 *   4. Resolve the current practitioner role.
 *   5. `encounter = facade.upsert({ ... })`.
 *   6. If `activityContext` and (no existing encounter or status is
 *      `in-progress`), run `handleActivityCompletion`.
 *   7. Show success / warning notification.
 *   8. `onSubmitted` + `onSuccess`-or-navigate.
 *   9. Catch + show error notification.
 *
 * Only the discriminator `type`, optional `originalFormData`, and the
 * "encounter" vs "discharge" notification copy varied between the five.
 * Centralising here turns each per-type submit hook into a 10-line
 * wrapper without altering the FHIR transaction or notification
 * semantics.
 *
 * Intake submit hooks remain distinct (they own the scribe encounter
 * ID-load on submit and the `complete-intake` chain).
 *
 * @agentic connections
 */

import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  Encounter,
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";

import type { CareTimeMethod } from "@benchmark/core/constants";
import { createEncounterFacade } from "@/facades/encounter.facade";
import { handleActivityCompletion } from "@benchmark/core/workflows";
import { toBundleInput } from "@benchmark/core/adapters";
import { createCareTimeFacade } from "@/facades/care-time.facade";
import {
  getEncounterCompletionLabel,
  getPatientDisplayName,
  getPractitionerDisplayName,
} from "@benchmark/core/resolvers";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "@benchmark/ui/utils";
import type {
  EncounterActivityContext,
  EncounterFormDataByType,
  TypedEncounterCode,
} from "@benchmark/core/types";

/**
 * Subset of `TypedEncounterCode` that is *not* an intake. Intake encounters
 * (clinical / cocm) own scribe ID-load and the `complete-intake` chain,
 * which are too different to share with this helper.
 *
 * @agentic connections
 */
export type NonIntakeEncounterCode = Exclude<
  TypedEncounterCode,
  | "clinical-intake-interaction"
  | "cocm-intake-interaction"
  | "unbilled-intake-interaction"
>;

/**
 * Notification copy variants. Discharge encounters say "Discharge" instead
 * of "Encounter" in success / warning copy. The activity-completion
 * `statusReason` is uniform across variants — a human-friendly encounter
 * label built by `getEncounterCompletionLabel`.
 *
 * @agentic connections
 */
export type SuccessCopyVariant = "default" | "discharge";

/**
 * Options for {@link useEncounterUpsertSubmit}.
 *
 * @agentic connections
 */
export type UseEncounterUpsertSubmitOptions<T extends NonIntakeEncounterCode> =
  {
    /** Discriminator for the typed form. */
    type: T;
    /** Resolved patient (from the form scaffold). */
    patient: Patient | undefined;
    /** Existing encounter when editing or resuming a draft. */
    existingEncounter?: Encounter;
    /** Pre-edit form values; powers diff-aware upsert for follow-up forms. */
    originalFormData?: EncounterFormDataByType[T];
    /** PractitionerRole resources to look up the current user's role. */
    practitionerRoles?: PractitionerRole[];
    /** Activity context for completion handshake (optional). */
    activityContext?: EncounterActivityContext;
    /** Selects "encounter" vs "discharge" notification copy. */
    successCopyVariant?: SuccessCopyVariant;
    /** Fires after a successful save with the new encounter id. */
    onSuccess?: (encounterId: string) => void;
    /** Fires after a successful save (regardless of `onSuccess`). */
    onSubmitted?: () => void;
    /**
     * Optional accessor for the captured care-time minutes (D5). When
     * the typed form renders `CareTimeActivityDurationField` on its
     * last step, this getter returns the current value at submit time
     * so the hook can tee a care-time Observation alongside the
     * Encounter save. Best-effort — failures are logged but never
     * roll back the encounter.
     */
    getCareTimeMinutes?: () => number | undefined;
    /** `Observation.method` to stamp on the tee'd care-time entry. */
    careTimeMethod?: CareTimeMethod;
    /**
     * Optional accessor for the linked Dialpad call's `Communication` id (from
     * the call picker). When present it is stamped on the care-time entry's
     * `derivedFrom` for traceability — the auditable link between the logged
     * minutes and the specific call they document.
     */
    getCareTimeCommunicationId?: () => string | undefined;
    /** Optional accessor for the linked call's effective period. */
    getCareTimeEffectivePeriod?: () =>
      { start: string; end: string } | undefined;
  };

const successPhrase = (variant: SuccessCopyVariant): string =>
  variant === "discharge" ? "Discharge" : "Encounter";

/**
 * Returns a memoised submit handler for a non-intake typed encounter form.
 * The returned callback accepts `EncounterFormDataByType[T]` and runs the
 * standard non-intake save flow.
 *
 * @agentic connections
 */
export const useEncounterUpsertSubmit = <T extends NonIntakeEncounterCode>({
  type,
  patient,
  existingEncounter,
  originalFormData,
  practitionerRoles,
  activityContext,
  successCopyVariant = "default",
  onSuccess,
  onSubmitted,
  getCareTimeMinutes,
  careTimeMethod,
  getCareTimeCommunicationId,
  getCareTimeEffectivePeriod,
}: UseEncounterUpsertSubmitOptions<T>) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const navigate = useNavigate();

  return useCallback(
    async (formData: EncounterFormDataByType[T]) => {
      if (!profile || !patient) {
        showErrorNotification(
          "Missing required data. Please refresh the page and try again.",
        );
        return;
      }

      // Provenance stamp is create-only: edits preserve the encounter's
      // original stamp, and a pre-remediation encounter's absent stamp stays
      // meaningful (mixed-era contract — absence ≠ free-form).
      const flat = {
        ...toBundleInput(type, formData),
        ...(!existingEncounter && {
          _encounterOrigin: activityContext
            ? ("activity-completion" as const)
            : ("free-form" as const),
        }),
      };
      const originalFlat = originalFormData
        ? toBundleInput(type, originalFormData)
        : undefined;

      try {
        const facade = createEncounterFacade(medplum);
        const myRole = (practitionerRoles ?? []).find(
          (role) =>
            role.practitioner?.reference === `Practitioner/${profile.id}`,
        );

        const encounter = await facade.upsert({
          formData: flat,
          patient,
          practitioner: profile,
          practitionerRole: myRole,
          existingEncounter,
          originalFormData: originalFlat,
          onWarning: (msg) => showWarningNotification(msg),
        });

        if (
          activityContext &&
          (!existingEncounter || existingEncounter.status === "in-progress")
        ) {
          try {
            const carePlan = await medplum.readResource(
              "CarePlan",
              activityContext.carePlanId,
            );
            await handleActivityCompletion(medplum, {
              carePlan,
              activityCode: activityContext.activityCode,
              taskId: activityContext.taskId,
              statusReason: `Completed via ${getEncounterCompletionLabel(encounter)}`,
              ...(encounter.id && {
                documentedEncounter: {
                  id: encounter.id,
                  display: getEncounterCompletionLabel(encounter),
                },
              }),
              completionData: {
                nextActivityOwnerRef:
                  (formData as { "followup-assigned-to"?: string })[
                    "followup-assigned-to"
                  ] || undefined,
                nextActivityOwnerDisplay:
                  (formData as { "followup-assigned-to-display"?: string })[
                    "followup-assigned-to-display"
                  ] || undefined,
              },
              practitionerRole: myRole,
            });
            showSuccessNotification(
              `${successPhrase(successCopyVariant)} saved and activity completed. Next step advanced.`,
            );
          } catch (activityError) {
            console.error(
              `Activity completion failed after ${successCopyVariant === "discharge" ? "discharge" : "encounter"} save:`,
              activityError,
            );
            showWarningNotification(
              `${successPhrase(successCopyVariant)} saved successfully, but activity completion failed. Please complete the task manually.`,
            );
          }
        } else if (successCopyVariant === "discharge") {
          showSuccessNotification(
            `Discharge ${existingEncounter ? "updated" : "created"} successfully.`,
          );
        } else {
          showSuccessNotification(
            `Encounter ${existingEncounter ? "updated" : "created"} successfully. All clinical data has been saved.`,
          );
        }

        // Best-effort care-time tee — failure logged, never blocks
        // the post-save navigation. Mirrors the
        // `logActivityProgress` tee pattern in
        // `packages/core/src/actions/logActivityProgress.action.ts`.
        const minutes = getCareTimeMinutes?.();
        if (
          minutes &&
          minutes >= 1 &&
          careTimeMethod &&
          patient.id &&
          encounter.id
        ) {
          const myRoleForTee = (practitionerRoles ?? []).find(
            (role) =>
              role.practitioner?.reference === `Practitioner/${profile.id}`,
          );
          if (myRoleForTee?.id) {
            const communicationId = getCareTimeCommunicationId?.();
            const period = getCareTimeEffectivePeriod?.();
            try {
              await createCareTimeFacade(medplum).upsert({
                formData: {
                  minutes,
                  method: careTimeMethod,
                  effectiveDate: period?.start ?? new Date().toISOString(),
                  ...(period && {
                    effectivePeriodStart: period.start,
                    effectivePeriodEnd: period.end,
                  }),
                  patientId: patient.id,
                  patientDisplay: getPatientDisplayName(patient),
                  performerRoleId: myRoleForTee.id,
                  performerDisplay: getPractitionerDisplayName(profile),
                  encounterId: encounter.id,
                  ...(communicationId && { communicationId }),
                  // Anchor activity-driven minutes to their Task/CarePlan so
                  // the entry is queryable by plan (builder stamps focus/
                  // basedOn from these — same shape as the intake in-bundle
                  // and progress-log tees).
                  ...(activityContext && {
                    taskId: activityContext.taskId,
                    carePlanId: activityContext.carePlanId,
                  }),
                },
              });
            } catch (err) {
              console.error("Care-time tee failed for encounter:", err);
            }
          }
        }

        onSubmitted?.();

        if (onSuccess && encounter.id) {
          onSuccess(encounter.id);
        } else {
          navigate(`/Patient/${patient.id}/Encounter/${encounter.id}`)?.catch(
            (error) => {
              console.error(
                `Failed to navigate after ${successCopyVariant === "discharge" ? "discharge" : "encounter"} save:`,
                error,
              );
            },
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        showErrorNotification(errorMessage);
        console.error(
          `${successCopyVariant === "discharge" ? "Discharge" : "Encounter"} save failed:`,
          error,
        );
      }
    },
    [
      profile,
      patient,
      medplum,
      navigate,
      existingEncounter,
      originalFormData,
      onSuccess,
      onSubmitted,
      practitionerRoles,
      activityContext,
      type,
      successCopyVariant,
      getCareTimeMinutes,
      careTimeMethod,
      getCareTimeCommunicationId,
      getCareTimeEffectivePeriod,
    ],
  );
};
